import {ThemeColorPath, CSSDimension} from 'platform/foundation';

import {is} from 'ramda';

import i18n from '@dms/i18n';

import {buildArray} from 'shared/web';

import {OpportunityType} from '../hooks/useRedirectFromOpportunities/types/OpportunityType';
import {TypeSegment} from '../hooks/useRedirectFromOpportunities/types/TypeSegment';

type ColumnTransformation = {
  title: string;
  tooltip: string;
  value: number;
  color: ThemeColorPath;
  height: CSSDimension;
  id: OpportunityType;
};

type OpportunitiesFunnelData =
  | {
      activeInterests: number;
      closed: number;
      contracts: number;
      newInterests: number;
      offers: number;
      sold?: number;
      bought?: number;
    }
  | undefined;

export function createOpportunitiesFunnelColumns(
  data: OpportunitiesFunnelData,
  isFunnelInterestsEnabled: boolean,
  isFunnelBusinessCasesEnabled: boolean,
  canReadInterest: boolean,
  typeSegment: TypeSegment
): ColumnTransformation[] {
  if (!data) {
    return [];
  }

  const maximum = Math.max(...Object.values(data));

  const calculateHeight = (value: number) => Math.round((value / maximum) * 100);

  const {activeInterests, closed, contracts, newInterests, offers, sold, bought} = data;

  return buildArray<ColumnTransformation>()
    .when(isFunnelInterestsEnabled && canReadInterest, {
      title: i18n.t('page.homepage.salesFunnel.labels.newInterests'),
      tooltip: i18n.t(`page.homepage.salesFunnel.tooltips.${typeSegment}.newInterests`),
      value: newInterests,
      color: 'palettes.orange.40.100',
      id: 'newInterests',
      height: `${calculateHeight(newInterests)}%`,
    })
    .when(isFunnelInterestsEnabled && canReadInterest, {
      title: i18n.t('page.homepage.salesFunnel.labels.activeInterests'),
      tooltip: i18n.t(`page.homepage.salesFunnel.tooltips.${typeSegment}.activeInterests`),
      value: activeInterests,
      color: 'palettes.magenta.40.100',
      id: 'activeInterests',
      height: `${calculateHeight(activeInterests)}%`,
    })
    .when(isFunnelInterestsEnabled, {
      title: i18n.t('page.homepage.salesFunnel.labels.offers'),
      tooltip: i18n.t(`page.homepage.salesFunnel.tooltips.${typeSegment}.offers`),
      value: offers,
      color: 'palettes.purple.40.100',
      id: 'offers',
      height: `${calculateHeight(offers)}%`,
    })
    .when(isFunnelBusinessCasesEnabled, {
      title: i18n.t('page.homepage.salesFunnel.labels.contracts'),
      tooltip: i18n.t(`page.homepage.salesFunnel.tooltips.${typeSegment}.contracts`),
      value: contracts,
      color: 'palettes.blue.60.100',
      id: 'contracts',
      height: `${calculateHeight(contracts)}%`,
    })
    .when(isFunnelBusinessCasesEnabled && is(Number, sold), {
      title: i18n.t('page.homepage.salesFunnel.labels.sold'),
      tooltip: i18n.t('page.homepage.salesFunnel.tooltips.selling.sold'),
      value: sold as number,
      color: 'palettes.teal.40.100',
      id: 'salesFunnel',
      height: `${calculateHeight(sold as number)}%`,
    })
    .when(isFunnelBusinessCasesEnabled && is(Number, bought), {
      title: i18n.t('page.homepage.salesFunnel.labels.bought'),
      tooltip: i18n.t('page.homepage.salesFunnel.tooltips.buying.bought'),
      value: bought as number,
      color: 'palettes.teal.40.100',
      id: 'salesFunnel',
      height: `${calculateHeight(bought as number)}%`,
    })
    .when(isFunnelBusinessCasesEnabled, {
      title: i18n.t('page.homepage.salesFunnel.labels.closed'),
      tooltip: i18n.t(`page.homepage.salesFunnel.tooltips.${typeSegment}.closed`),
      value: closed,
      color: 'palettes.green.50.100',
      id: 'closed',
      height: `${calculateHeight(closed)}%`,
    });
}
